/**
 * StilstandView — kleden die te lang stil staan in een bewerkingsstatus terwijl
 * de werkorder nog in de wasserij loopt. Signaleert overgeslagen field-app-
 * registraties vóór de klant belt. Data: GET /api/wasserij/stilstand (mirror).
 *
 * Werk-lijst: klik opent de wasserij-item-drawer mét blader-context zodat de
 * lijst item-voor-item verwerkt kan worden zonder de pagina te verlaten.
 * Bijgewerkte items blijven gedimd staan tot de mirror-sync ze laat vervallen
 * (sync loopt ~5 min achter op een write; eerlijk tonen, niet doen alsof).
 */

import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { StilstandItem } from '../../stores/wasserij-store';
import { useWasserijItemDrawerStore } from '../../stores/wasserij-item-drawer-store';
import { useOrderDetailDrawerStore } from '../../stores/order-detail-drawer-store';

interface StilstandViewProps {
  items: StilstandItem[];
  isLoading: boolean;
  stale: boolean;
  syncedAt: string | null;
}

/** Kleurcode: wat de klant raakt eerst (rit gepland, broers al ingepakt), dan duur t.o.v. de drempel. */
function severity(item: StilstandItem): { dot: string; text: string } {
  if (item.afleverritGepland || item.broersVerder > 0 || item.daysStuck >= 2 * item.drempelDagen) {
    return { dot: 'bg-red-500', text: 'text-red-600' };
  }
  return { dot: 'bg-amber-500', text: 'text-amber-600' };
}

export function StilstandView({ items, isLoading, stale, syncedAt }: StilstandViewProps) {
  const openItem = useWasserijItemDrawerStore((s) => s.open);
  const openItemId = useWasserijItemDrawerStore((s) => s.openItemId);
  const openOrder = useOrderDetailDrawerStore((s) => s.open);

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Items die net via de drawer zijn bijgewerkt: dim + badge tot de mirror-sync
  // ze uit de lijst laat vallen (read-your-write is hier bewust niet gefaked).
  const [touchedIds, setTouchedIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    const handler = (e: Event) => {
      const itemId = (e as CustomEvent<{ itemId?: string }>).detail?.itemId;
      if (!itemId) return;
      setTouchedIds((prev) => {
        if (prev.has(itemId)) return prev;
        const next = new Set(prev);
        next.add(itemId);
        return next;
      });
    };
    window.addEventListener('wasserij-item:updated', handler);
    return () => window.removeEventListener('wasserij-item:updated', handler);
  }, []);

  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      counts.set(item.itemStatus, (counts.get(item.itemStatus) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const visible = statusFilter ? items.filter((i) => i.itemStatus === statusFilter) : items;

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-emerald-600 font-medium">Geen vastgelopen kleden</p>
        <p className="text-muted text-sm mt-1">
          Geen kleed staat langer dan normaal in zijn status.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted">
          <span className="font-semibold text-main">{items.length}</span> kleed
          {items.length === 1 ? '' : 'en'} langer in hun status dan normaal (Drogen 6 dagen, Klaar voor Reinigen 5),
          terwijl de order nog loopt. Meestal fysiek verder maar niet gescand; zolang staat de order stil en krijgt
          de klant geen aflevermail. Klik een kleed om het in de drawer te verwerken.
        </p>
        {stale && syncedAt && (
          <span className="text-xs text-amber-600" title="Mirror-sync is mogelijk verouderd">
            sync {format(parseISO(syncedAt), 'HH:mm', { locale: nl })}
          </span>
        )}
      </div>

      {/* Status-filter — alleen tonen als er iets te filteren valt */}
      {statusCounts.size > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <FilterChip
            label={`Alle (${items.length})`}
            active={statusFilter === null}
            onClick={() => setStatusFilter(null)}
          />
          {[...statusCounts.entries()].map(([status, count]) => (
            <FilterChip
              key={status}
              label={`${status} (${count})`}
              active={statusFilter === status}
              onClick={() => setStatusFilter(statusFilter === status ? null : status)}
            />
          ))}
        </div>
      )}

      <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
        {visible.map((item) => {
          const sev = severity(item);
          const touched = touchedIds.has(item.id);
          const isOpen = openItemId === item.id;
          return (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => openItem(item.id, visible.map((i) => i.id))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openItem(item.id, visible.map((i) => i.id));
                }
              }}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer ${
                isOpen ? 'bg-indigo-50/50' : ''
              } ${touched ? 'opacity-50' : ''}`}
            >
              {/* Severity dot */}
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sev.dot}`} />

              {/* Main info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-main">{item.ref}</span>
                  <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium border border-indigo-100">
                    {item.itemStatus}
                  </span>
                  {item.afleverritGepland && (
                    <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-medium border border-red-100">
                      afleverrit gepland
                    </span>
                  )}
                  {item.broersVerder > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-medium border border-red-100">
                      {item.broersVerder} ander{item.broersVerder === 1 ? '' : 'e'} kle{item.broersVerder === 1 ? 'ed' : 'den'} al ingepakt
                    </span>
                  )}
                  {item.klantNaam && (
                    <span className="text-sm text-muted truncate">{item.klantNaam}</span>
                  )}
                  {touched && (
                    <span
                      className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium border border-emerald-100"
                      title="Zojuist bijgewerkt — verdwijnt uit de lijst na de eerstvolgende mirror-sync (~5 min)"
                    >
                      bijgewerkt
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted">
                  {item.woRef && item.woId ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOrder(item.woId!);
                      }}
                      className="text-indigo-600 hover:underline"
                      title="Open order-detail"
                    >
                      Order {item.woRef}
                    </button>
                  ) : (
                    item.woRef && <span>Order {item.woRef}</span>
                  )}
                  <span>WO: {item.woStatus}</span>
                  {item.lastHandling && <span>Laatste: {item.lastHandling}</span>}
                  {item.oppervlakteM2 > 0 && <span>{item.oppervlakteM2.toFixed(1)} m²</span>}
                </div>
              </div>

              {/* Days stuck + drawer-affordance */}
              <div className="text-right flex-shrink-0">
                <span className={`text-sm font-semibold ${sev.text}`}>{item.daysStuck}d</span>
                <p className="text-[11px] text-muted">normaal {'<'} {item.drempelDagen}d</p>
              </div>
              <svg
                className="w-4 h-4 text-slate-300 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-indigo-600 text-white border-indigo-600'
          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
      }`}
    >
      {label}
    </button>
  );
}
